import { css } from '@emotion/css';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loading } from '../../shared/Loading';
import { DialogRoute } from '../../shared/routes/DialogRoute';
import { TitleHeader } from '../../shared/TitleHeader';
import { SettingsListHintDialog } from './SettingsListHintDialog';

const settingsStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  font-size: 16px;
  overflow: auto;

  .body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 480px;
    overflow-y: scroll;
    margin: 0 auto;
    padding: 24px 0 0;
  }
  .menu-items {
    flex: 1 1 auto;
    list-style: none;
  }
  footer {
    flex: 0 0 auto;
    padding: 12px 0 48px;
    p {
      font-size: 12px;
      color: #938666;
      text-align: center;
    }
  }
`;

interface SettingsListItemProps {
  id: string;
  title: string;
  path: string;
}

const SettingsListItem: React.FC<SettingsListItemProps> = ({ id, title, path }) => {
  const { t } = useTranslation('settings');
  return (
    <li
      className={css`
        position: relative;
        padding: 2px 0 0;
        &:nth-child(n + 2)::after {
          content: '';
          position: absolute;
          top: 0;
          right: 20px;
          left: 20px;
          border-bottom: 2px dashed #dcd8bf;
        }
        .content {
          padding: 0 20px;
          transition: background 0.1s;
          border-radius: 2px;
          &:active {
            background: #ece5d4;
          }
        }
        a {
          display: block;
          text-decoration: none;
        }
        button {
          width: 100%;
          text-align: left;
          text-align: start;
          color: #4d3906;
          padding: 16px 8px;
        }
      `}
    >
      <div className="content">
        <Link to={path} aria-labelledby={id}>
          <button id={id} aria-hidden={true}>
            {t(title)}
          </button>
        </Link>
      </div>
    </li>
  );
};

const items: SettingsListItemProps[] = [
  {
    id: 'reselect_user',
    title: 'reselect_user',
    path: '/players/settings/switch-user'
  },
  {
    id: 'language',
    title: 'language',
    path: '/players/settings/language'
  },
  {
    id: 'license',
    title: 'license',
    path: '/players/settings/license'
  },
  {
    id: 'credit',
    title: 'credit',
    path: '/players/settings/credit'
  }
];

export const SettingsList = () => {
  const { t } = useTranslation(['settings', 'translation']);

  return (
    <>
      <main className={settingsStyles}>
        <TitleHeader
          title={t('top')}
          color="#dbcca7"
          hintPath={'/players/settings/hint'}
          hintLabel={t('help')}
          backLabel={t('translation:aria_button_back_to_home')}
        />
        <div className="body">
          <ul className="menu-items">
            {items.map(item => (
              <SettingsListItem key={item.id} {...item} />
            ))}
          </ul>
          {<DebugMenuComponent />}
          <footer>
            <p>{`v${process.env.REACT_APP_VERSION}`}</p>
          </footer>
        </div>
      </main>
      <DialogRoute path="/players/settings/hint">
        <SettingsListHintDialog />
      </DialogRoute>
    </>
  );
};

const DebugMenu = React.lazy(() => import('../DebugMenu'));

class DebugMenuComponent extends React.Component<Record<string, unknown>, { hasError?: boolean }> {
  constructor(props: Record<string, unknown>) {
    super(props);
    this.state = { hasError: false };
  }
  public static getDerivedStateFromError(_: any) {
    return { hasError: true };
  }
  public render() {
    if (process.env.REACT_APP_DEBUG_OPTION !== 'true') {
      return <></>;
    }
    if (this.state.hasError) {
      return <></>;
    }
    return (
      <Suspense fallback={<Loading />}>
        <DebugMenu />
      </Suspense>
    );
  }
}
